import { useRef, useState } from "react";
import PropTypes from "prop-types";
import { FiUpload } from "react-icons/fi";
import { FaFileAlt } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import useNotification from "../../hooks/useNotification";
import styles from "./styles.module.css";

const MAX_FILE_SIZE_MB = 4;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const FileUpload = ({
  label,
  accept = ".csv",
  allowedExtensions = [".csv"],
  helperText = ["CSV files only"],
  error,
  onFileSelect,
  onError,
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const { errorToast } = useNotification();

  const resetInput = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isValidFileType = (file) => {
    if (!allowedExtensions.length) return true;
    const lowerCaseName = file.name.toLowerCase();
    return allowedExtensions.some((ext) =>
      lowerCaseName.endsWith(ext.toLowerCase()),
    );
  };

  const updateFile = (file) => {
    if (!file) return;

    if (!isValidFileType(file)) {
      resetInput();
      if (onError) {
        onError(`Only ${allowedExtensions.join(", ")} files are allowed.`);
      }
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      resetInput();
      if (onError) {
        onError(`File size should not exceed ${MAX_FILE_SIZE_MB} MB.`);
      }
      return;
    }

    if (onError) onError("");
    setSelectedFile(file);
    if (onFileSelect) onFileSelect(file);
  };

  const handleClick = () => fileInputRef.current?.click();

  const handleChange = (e) => {
    updateFile(e.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();

    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;

    updateFile(files[0]);

    if (files.length > 1) {
      errorToast("Only one file can be uploaded at a time.");
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDelete = (e) => {
    e.stopPropagation();
    resetInput();
    if (onFileSelect) onFileSelect(null);
    if (onError) onError("");
  };

  return (
    <div className={styles.wrapper}>
      {label && <label className={styles.label}>{label}</label>}

      {selectedFile ? (
        <div className={styles.fileCard}>
          <FaFileAlt className={styles.fileIcon} />
          <div className={styles.fileInfo}>
            <span className={styles.fileName}>{selectedFile.name}</span>
            <span className={styles.fileSize}>
              {formatFileSize(selectedFile.size)}
            </span>
          </div>
          <button
            className={styles.deleteButton}
            onClick={handleDelete}
            aria-label="Remove file"
            type="button"
          >
            <MdDeleteOutline size={24} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.uploadBox}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            className={styles.input}
            onChange={handleChange}
          />
          <FiUpload className={styles.icon} />
          <p className={styles.primaryText}>Click to upload or drag and drop</p>
          <div className={styles.helperTextContainer}>
            {helperText.map((text, index) => (
              <p key={index} className={styles.helperText}>
                {text}
              </p>
            ))}
          </div>
        </button>
      )}

      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  );
};

FileUpload.propTypes = {
  label: PropTypes.string,
  accept: PropTypes.string,
  allowedExtensions: PropTypes.arrayOf(PropTypes.string),
  helperText: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  ),
  error: PropTypes.string,
  onFileSelect: PropTypes.func,
  onError: PropTypes.func,
};

export default FileUpload;
